import { AfterViewInit, Component, Input, OnInit, ViewChild } from '@angular/core';
import { UntypedFormControl, Validators } from '@angular/forms';
import { createForm, FormType, subformComponentProviders } from 'ngx-sub-form';
import { FlowType } from 'src/app/perical/models/flow.model';
import { Fluid } from '../../../../../models/fluid.model';
import { UserUnitsModel } from 'src/app/core/components/user-units/user-units.model';
import { unitsLib } from 'src/app/core/services/unit-library';
import { InputNumber } from 'primeng/inputnumber';

export interface HydrocarbonFlowForm {
  type: FlowType.HYDROCARBON;
  oilFlowRate: number;
  gasFlowRate: number;
  waterFlowRate: number;
  multiphaseCorrelation: string;
  gasModel: string;
  pipeRoughness: number;
  co2EosOption: string;
  gasToOilRatio: string;
}

@Component({
    selector: 'app-hydrocarbon-fluid',
    templateUrl: './hydrocarbon-fluid.component.html',
    providers: subformComponentProviders(HydrocarbonFluidComponent),
    standalone: false
})
export class HydrocarbonFluidComponent implements OnInit, AfterViewInit {
  public control(name: string): UntypedFormControl {
    return this.form.formGroup.get(name) as UntypedFormControl;
  }

  constructor() {
    this.multiphaseCorrelations = [
      { name: 'Beggs & Brill', value: 'beggsBrill' },
      { name: 'Gray', value: 'gray' },
      { name: 'Hagedorn & Brown', value: 'hagedornBrown' },
      // { name: 'Zhang Mechanistic', value: 'zhangMechanistic' }
    ];

    this.gasModels = [
      { name: 'SRK', value: 'srk' }
    ];

    this.co2EosOptions = [
      { name: 'GERG-2008', value: 'GERG2008' },
      { name: 'PVT Import File', value: 'ImportFile' },
      // { name: 'Soave-BWR', value: 'SoaveBWR' },
      // { name: 'Peng-Robinson', value: 'PR' }
    ];

    this.gorOptions = [];
  }

  @Input()
  public isGasLiftFlow: boolean;

  @Input()
  public userUnits: UserUnitsModel;

  @Input()
  public isReadOnly: boolean;

  @Input()
  public includeRestartOperation: boolean;

  @ViewChild('gorInput') gorInput!: InputNumber;
  
  public flowUnit: string;
  public gasFlowUnit: string;
  public gorUnit: string;
  public oilFlowRateValidation: { min: number, max: number };
  public waterFlowRateValidation: { min: number, max: number };
  public gasFlowRateValidation: { min: number, max: number };

  public multiphaseCorrelationTooltip = `
      The following considerations may help in choosing multiphase correlation: \n
    * For highly deviated (inc > 45°) production wells or for injection wells with “downhill” multiphase flow, Beggs & Brill should be used. \n
    * Hagedorn & Brown may be best for vertical production wells with liquid dominant flow. \n
    * Gray is appropriate for high GLR or low CGR multiphase flow where gas phase dominates. \n
    * For single-phase flow (all liquid or all gas), flow is essentially Newtonian, and choice of multiphase correlation is ignored.
  `;

  public oilGas: boolean[] = [false, false];
  @Input('selectedFluid') set _selectedFluid(selectedFluid: Fluid) {
    this.oilGas[0] = selectedFluid.state['oilApiGravity'] > 0 ? true : false;
    this.oilGas[1] = selectedFluid.state['gasComposition'] || selectedFluid.state.type === 'co2Fluid' ? true : false;
    this.vleSelected = selectedFluid.state.type === 'vle' ? true : false;
    this.gasModelHidden = selectedFluid.state.type === 'vle' ? true : false;
    this.co2FluidSelected = selectedFluid.state.type === 'co2Fluid' ? true : false;
    this._hasGas = selectedFluid.state['gasComposition'] ? true : false;

    this.form.formGroup.controls.gasFlowRate.reset();
    this.form.formGroup.controls.oilFlowRate.reset();
    this.form.formGroup.controls.waterFlowRate.reset();

    if (this.userUnits) {
      this.checkFluidTypeAndValidation();
    }

    this.calculateGor();
  }

  public pipeRoughnessValidation: { min: number, max: number };
  public multiphaseCorrelations: any[];
  public co2EosOptions: any[];
  public gasModels: any[];
  public gorOptions: any[];
  public emitInitialValueOnInit = false;
  // public emitNullOnDestroy = false;
  public gor: number;
  private selectedOption;
  public gorEnabled: boolean;
  public gasRateDisabled: boolean;
  public oilRateDisabled: boolean;
  public oilHidden: boolean;
  public gasHidden: boolean;
  public gasModelHidden = false;
  public vleSelected: boolean;
  public co2FluidSelected: boolean;

  private _hasGas: boolean;
  private _defaultPipeRoughness: number;
  private _dataBackup: any | null = null;

  public form = createForm<HydrocarbonFlowForm>(this, {
    formType: FormType.SUB,
    formControls:  {
      type: new UntypedFormControl(FlowType.HYDROCARBON),
      oilFlowRate: new UntypedFormControl(null, { updateOn: 'blur', validators: [Validators.required] }),
      gasFlowRate: new UntypedFormControl(null, { updateOn: 'blur', validators: [Validators.required] }),
      waterFlowRate: new UntypedFormControl(null, { updateOn: 'blur', validators: [Validators.required] }),
      multiphaseCorrelation: new UntypedFormControl('beggsBrill', Validators.required),
      gasModel: new UntypedFormControl('srk', [Validators.required]),
      pipeRoughness: new UntypedFormControl(null, { updateOn: 'blur', validators: [Validators.required] }),
      co2EosOption: new UntypedFormControl('GERG2008'),
      gasToOilRatio: new UntypedFormControl(null)
    },
    fromFormGroup: (formValue: HydrocarbonFlowForm | null) => {

      const controls = this.form.formGroup.controls;
      const check = (control, field) => control?.invalid || (control?.touched && this._dataBackup?.[field] === formValue[field]);

      if (check(controls.oilFlowRate, 'oilFlowRate') || 
          check(controls.gasFlowRate, 'gasFlowRate') ||
          check(controls.waterFlowRate, 'waterFlowRate') ||
          check(controls.pipeRoughness, 'pipeRoughness')) {
        
        controls.oilFlowRate.markAsUntouched();
        controls.gasFlowRate.markAsUntouched();
        controls.waterFlowRate.markAsUntouched();
        controls.pipeRoughness.markAsUntouched();
        return;
      }

      this._dataBackup = formValue;

      return this.form.formGroup.getRawValue();
    }
  });

  ngOnInit(): void {
    this._defaultPipeRoughness = 0.001968;
    if (this.userUnits.shortLengths == 'in') {
      this.pipeRoughnessValidation = { min: 0, max: 0.25 };
    } else if (this.userUnits.shortLengths == 'cm') {
      this.pipeRoughnessValidation = { min: 0, max: 0.6 };
      this._defaultPipeRoughness = 0.00499;
    } else {
      this.pipeRoughnessValidation = { min: 0, max: 6.3 };
      this._defaultPipeRoughness = 0.0499;
    }
    this.form.formGroup.controls.pipeRoughness.setValidators([Validators.min(this.pipeRoughnessValidation.min), Validators.max(this.pipeRoughnessValidation.max), Validators.required]);

    this.flowUnit = unitsLib[this.userUnits.hydrocarbonFlow].symbol;
    this.gasFlowUnit = unitsLib[this.userUnits.gasInjectionRate].symbol;
  }

  ngAfterViewInit(): void {
    if (this.form.formGroup.controls.pipeRoughness.value == null) {
      this.form.formGroup.controls.pipeRoughness.patchValue(this._defaultPipeRoughness, { emitEvent: false });
    }

    this._dataBackup = this.form.formGroup.value;

    const controls = this.form.formGroup.controls;
    if (!controls.oilFlowRate.value) { controls.oilFlowRate.setValue(this.co2FluidSelected || this.oilHidden ? 0 : controls.oilFlowRate.value, { emitEvent: false }); }
    if (!controls.gasFlowRate.value) { controls.gasFlowRate.setValue(this._hasGas ? controls.gasFlowRate.value : 0, { emitEvent: false }); }
    if (!controls.waterFlowRate.value) { controls.waterFlowRate.setValue(this.co2FluidSelected ? 0 : controls.waterFlowRate.value, { emitEvent: false }); }

    this.checkFluidTypeAndValidation();
    this.calculateGor();
    this.onGorTypeChange({ value: this.form.formGroup.controls.gasToOilRatio.value });
  }

  private checkFluidTypeAndValidation(): void {
    const oilWaterUnit = this.userUnits.hydrocarbonFlow.split('/')[0];
    if (this.userUnits.gasInjectionRate == 'scfh') {
      this.gorUnit = `scf/${oilWaterUnit}`;
      this.gasFlowRateValidation = { min: 0, max: 1000 };
    } else {
      this.gorUnit = `m³/${oilWaterUnit}`;
      this.gasFlowRateValidation = { min: 0, max: 28.3 };
    }

    if (this.userUnits.gasInjectionRate.endsWith('Base')) {
      this.gasFlowRateValidation.max = this.gasFlowRateValidation.max * 1000000;
    }

    if (this.co2FluidSelected) {
      this.form.formGroup.controls.waterFlowRate.setValidators(null);
      this.form.formGroup.controls.oilFlowRate.setValidators(null);
      this.form.formGroup.controls.waterFlowRate.setValue(0, { emitEvent: false });
      this.form.formGroup.controls.oilFlowRate.setValue(0, { emitEvent: false });
      this.gasHidden = false;
      this.oilHidden = true;
      this.gasModelHidden = true;
      this.form.formGroup.controls.gasToOilRatio.setValue('gasWater', { emitEvent: false });
      this.form.formGroup.controls.gasFlowRate.setValidators([Validators.required, Validators.min(this.gasFlowRateValidation.min), Validators.max(this.gasFlowRateValidation.max)]);
    } else {
      if (this.userUnits.hydrocarbonFlow == 'bbl/d') {
        this.oilFlowRateValidation = { min: 0, max: 1440000 };
        this.waterFlowRateValidation = { min: 0, max: 100000 };
      } else {
        this.oilFlowRateValidation = { min: 0, max: 228941 };
        this.waterFlowRateValidation = { min: 0, max: 15898 };
      }

      this.form.formGroup.controls.waterFlowRate.setValidators([Validators.required, Validators.min(this.waterFlowRateValidation.min), Validators.max(this.waterFlowRateValidation.max)]);

      if (this.oilGas[0] == true && !this.oilGas[1] == true && !this.co2FluidSelected) {
        this.form.formGroup.controls.gasFlowRate.setValidators(null);
        this.form.formGroup.controls.gasFlowRate.setValue(0, { emitEvent: false });
        this.form.formGroup.controls.gasFlowRate.markAllAsTouched();
        this.gasHidden = true;
      } else {
        this.gasHidden = false;
        this.form.formGroup.controls.gasFlowRate.setValidators([Validators.required, Validators.min(this.gasFlowRateValidation.min), Validators.max(this.gasFlowRateValidation.max)]);
      }

      if (!this.oilGas[0] == true && this.oilGas[1] == true) {
        this.form.formGroup.controls.oilFlowRate.setValidators(null);
        this.form.formGroup.controls.oilFlowRate.setValue(0, { emitEvent: false });
        this.oilHidden = true;
        this.form.formGroup.controls.gasFlowRate.setValidators([Validators.required, Validators.min(this.gasFlowRateValidation.min), Validators.max(this.gasFlowRateValidation.max)]);
        this.form.formGroup.controls.gasFlowRate.updateValueAndValidity({ emitEvent: false });

        this.gorOptions = [
          { label: 'Gas & Water', value: 'gasWater' },
          { label: 'Gas & GWR', value: 'gasGor' },
          { label: 'Water & GWR', value: 'waterGor' }
        ];
        if (!this.form.formGroup.controls.gasToOilRatio.value || this.gorOptions.findIndex(option => option.value === this.form.formGroup.controls.gasToOilRatio.value) === -1) {
          this.form.formGroup.controls.gasToOilRatio.setValue('gasWater', { emitEvent: false });
        }
      } else {
        this.oilHidden = false;
        this.form.formGroup.controls.oilFlowRate.setValidators([Validators.required, Validators.min(this.oilFlowRateValidation.min), Validators.max(this.oilFlowRateValidation.max)]);
        this.form.formGroup.controls.oilFlowRate.updateValueAndValidity({ emitEvent: false });

        this.gorOptions = [
          { label: 'Oil & Gas', value: 'oilGas' },
          { label: 'Oil & GOR', value: 'oilGor' },
          { label: 'Gas & GOR', value: 'gasGor' }
        ];
        if (!this.form.formGroup.controls.gasToOilRatio.value || this.gorOptions.findIndex(option => option.value === this.form.formGroup.controls.gasToOilRatio.value) === -1) {
          this.form.formGroup.controls.gasToOilRatio.setValue('oilGas', { emitEvent: false });
        }
      }

      if (this.isGasLiftFlow) {
        // hide oil and water flow rates for gas lift operations
        this.form.formGroup.controls.oilFlowRate.setValidators(null);
        this.form.formGroup.controls.oilFlowRate.setValue(0, { emitEvent: false });
        this.oilHidden = true;
        this.form.formGroup.controls.waterFlowRate.setValidators(null);
        this.form.formGroup.controls.waterFlowRate.setValue(0, { emitEvent: false });
        this.form.formGroup.controls.gasToOilRatio.setValue('gasWater', { emitEvent: false });
        this.form.formGroup.controls.gasFlowRate.setValidators([Validators.required, Validators.min(this.gasFlowRateValidation.min), Validators.max(this.gasFlowRateValidation.max)]);
        this.form.formGroup.controls.gasFlowRate.updateValueAndValidity({ emitEvent: false });
        this.gasHidden = false;
        this.gasModelHidden = true;
      }
    }
  }

  onGorTypeChange(e) {
    // if (this.oilHidden || this.gasHidden) {
    //   return;
    // }
    this.selectedOption = e.value;
    switch (e.value) {
      case "gasGor": {
        this.form.formGroup.controls.oilFlowRate.disable({ emitEvent: false});
        this.form.formGroup.controls.gasFlowRate.enable({ emitEvent: false});
        if (this.oilHidden) {
          this.form.formGroup.controls.waterFlowRate.disable({ emitEvent: false});
        } else {
          this.form.formGroup.controls.waterFlowRate.enable({ emitEvent: false});
        }
        this.gorEnabled = false;
        break;
      }
      case "oilGor": {
        this.form.formGroup.controls.oilFlowRate.enable({ emitEvent: false});
        this.form.formGroup.controls.gasFlowRate.disable({ emitEvent: false});
        this.gorEnabled = false;
        break;
      }
      case "waterGor": {
        this.form.formGroup.controls.oilFlowRate.disable({ emitEvent: false});
        this.form.formGroup.controls.waterFlowRate.enable({ emitEvent: false});
        this.form.formGroup.controls.gasFlowRate.disable({ emitEvent: false});
        this.gorEnabled = false;
        break;
      }
      case "gasWater": {
        this.form.formGroup.controls.gasFlowRate.enable({ emitEvent: false});
        this.form.formGroup.controls.waterFlowRate.enable({ emitEvent: false});
        this.gorEnabled = true;
        break;
      }
      default: {
        this.form.formGroup.controls.oilFlowRate.enable({ emitEvent: false});
        this.form.formGroup.controls.gasFlowRate.enable({ emitEvent: false});
        this.form.formGroup.controls.waterFlowRate.enable({ emitEvent: false});
        this.gorEnabled = true;
        break;
      }
    }
  }

  updateGor(updatedValue: string): void {
    if (!this.gorInput) {
      return;
    }
    
    setTimeout(() => {
      const gorValue = this.gorInput.value ?? +this.gorInput.placeholder;
      const gasRate = !this.oilHidden ? +(gorValue * (this.form.formGroup.value.oilFlowRate ?? 0) / 1000000).toFixed(2) : +(gorValue * (this.form.formGroup.value.waterFlowRate ?? 0) / 1000000).toFixed(2);
      const oilRate = +((this.form.formGroup.value.gasFlowRate ?? 0) / gorValue * 1000000).toFixed(2);
      const waterRate = +((this.form.formGroup.value.gasFlowRate ?? 0) / gorValue * 1000000).toFixed(2);
      let gor = !this.oilHidden ? +((this.form.formGroup.value?.gasFlowRate ?? 0) * 1000000 / (this.form.formGroup.value?.oilFlowRate ?? 0)).toFixed(2) : +((this.form.formGroup.value?.gasFlowRate ?? 0) * 1000000 / (this.form.formGroup.value?.waterFlowRate ?? 0)).toFixed(2);

      if (Number.isNaN(gor)) {
        gor = 0;
      }

      switch (updatedValue) {
        case "gas": {
          if (this.gorEnabled) {
            this.gor = gor;
            break;
          }
          if (!this.oilHidden) {
          this.form.formGroup.controls.oilFlowRate.setValue(oilRate);
          } else {
            this.form.formGroup.controls.waterFlowRate.setValue(waterRate);
          }
          break;
        }
        case "oil": {
          if (this.gorEnabled) {
            this.gor = gor;
            break;
          }
          this.form.formGroup.controls.gasFlowRate.setValue(gasRate);
          break;
        }
        case "water": {
          if (this.gorEnabled) {
            this.gor = gor;
            break;
          }
          if (this.oilHidden) {
            this.form.formGroup.controls.gasFlowRate.setValue(gasRate);
          }
          break;
        }
        default: {
          if (this.selectedOption === 'oilGor') {
            this.form.formGroup.controls.gasFlowRate.setValue(gasRate);
          } else if (this.selectedOption === 'gasGor') {
            if (!this.oilHidden) {
              this.form.formGroup.controls.oilFlowRate.setValue(oilRate);
            } else {
              this.form.formGroup.controls.waterFlowRate.setValue(waterRate);
            }
          }
          this.gorEnabled = false;
          break;
        }
      }
    }, 50);
  }

  private calculateGor(): void {
    setTimeout(() => {
      this.gorEnabled = true;
      if (!this.oilHidden) {
        this.gor = +((this.form.formGroup.value?.gasFlowRate ?? 0) * (this.userUnits.gasInjectionRate.endsWith('Base') ? 1 : 1000000) / (this.form.formGroup.value?.oilFlowRate ?? 0)).toFixed(2);
      } else if (!this.co2FluidSelected) {
        this.gor = +((this.form.formGroup.value?.gasFlowRate ?? 0) * (this.userUnits.gasInjectionRate.endsWith('Base') ? 1 : 1000000) / (this.form.formGroup.value?.waterFlowRate ?? 0)).toFixed(2); 
      }

      if (Number.isNaN(this.gor)) {
        this.gor = 0;
      }
    }, 10);
  }
}
